/**
 * Sesiunea de comanda: adresa, vitrina aleasa, vanzatorul acceptat si liniile
 * de cos. Traieste in `localStorage` ca sa supravietuiasca unui refresh.
 *
 * Piesa care nu e cosmetica: `acceptedVendorId` (ADR-003). Vanzatorul e firma
 * care emite bonul, deci trebuie ARATAT clientului si acceptat de el inainte de
 * confirmare. Aici asta inseamna trei reguli:
 *
 *  - se sterge la orice schimbare de adresa sau de vitrina, fiindca oricare
 *    dintre ele poate schimba firma care gateste;
 *  - se seteaza doar prin `acceptSeller`, apelat din ecranul care chiar a
 *    afisat identitatea completa a firmei;
 *  - se trimite la checkout ca `acceptedSellerVendorId`; daca intre timp core-ul
 *    a ales alta firma, raspunsul e `SELLER_MISMATCH` si comanda NU pleaca.
 *
 * A doua piesa: liniile pot intra in cos INAINTE de adresa (din meniul generic).
 * Ele poarta `verifiedForAddressKey` — adresa pentru care au fost reconstruite
 * din meniul ei. O linie neverificata pentru adresa curenta se reverifica de
 * paginile care au meniul adresei (`revalidateCartLines`) inainte de finalizare;
 * ce nu se poate onora ramane in cos marcat `unavailableAtAddress`, vizibil.
 *
 * Cosul nu calculeaza niciun total care ajunge intr-o decizie. Subtotalul de
 * mai jos e o insumare pentru afisaj; sumele care conteaza vin din `quote`.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  MAX_QTY_PER_LINE,
  type CartSelectedOptionInput,
  type DropoffAddressDto,
  type MoneyMinor,
} from "@deliveryapp/shared";

export interface CartLine {
  /**
   * ADR-024: aceeasi oferta cu optiuni diferite e ALTA linie. Fara cheia asta,
   * "pizza cu blat pufos" si "pizza cu blat subtire" s-ar uni intr-un rand de
   * cantitate 2, iar bucataria n-ar avea de unde sti care bucata e care.
   */
  lineId: string;
  offerId: string;
  skuId: string;
  name: string;
  /** Pretul de baza al ofertei plus diferentele optiunilor alese. */
  unitPriceGross: MoneyMinor;
  /** Prețul de referință plus aceleași opțiuni, doar pentru afișarea economiei. */
  compareAtUnitPrice?: MoneyMinor | null;
  qty: number;
  notes: string | null;
  imageUrl: string | null;
  /** Ce se trimite la core pe linia de comanda. */
  selectedOptions: CartSelectedOptionInput[];
  /** Ce se arata clientului si se tipareste: «Blat: pufos». */
  optionLabels: string[];
  /**
   * Limita de cantitate a ofertei, purtata PE LINIE: pagina de cos nu are
   * oferta la indemana, doar linii salvate. Optional: liniile scrise de o
   * versiune anterioara nu il au.
   */
  maxQty?: number | null;
  /** Cantitatea minima a ofertei, purtata PE LINIE din acelasi motiv. */
  minQty?: number | null;
  /**
   * Adresa (cheie `lat:lng`) pentru care linia a fost reconstruita din meniul
   * adresei. `null`/absent = adaugata din meniul generic, inca neverificata.
   */
  verifiedForAddressKey?: string | null;
  /** Verificata si NEonorabila la adresa: ramane vizibila, blocheaza finalizarea. */
  unavailableAtAddress?: boolean;
}

/** Cantitatea aceleiași oferte din celelalte configurații/linii. */
export function otherQtyForOffer(lines: readonly CartLine[], offerId: string, lineId: string): number {
  return lines.reduce(
    (total, line) => total + (line.offerId === offerId && line.lineId !== lineId ? line.qty : 0),
    0,
  );
}

/** Cantitatea totală a ofertei, indiferent de configurația/modificatorii liniei. */
export function qtyForOffer(lines: readonly CartLine[], offerId: string): number {
  return lines.reduce((total, line) => total + (line.offerId === offerId ? line.qty : 0), 0);
}

export function qtyForNewCartLine(
  lines: readonly CartLine[],
  offerId: string,
  lineId: string,
  requestedQty: number,
  maxQty?: number | null,
): number {
  const ceiling = lineCeiling(maxQty);
  const remaining = Math.max(0, ceiling - otherQtyForOffer(lines, offerId, lineId));
  return Math.max(0, Math.min(requestedQty, remaining));
}

/**
 * Minimul comercial aparține ofertei, nu configurației. Folosim cel mai mare
 * minim văzut pe liniile persistate ca gard fail-closed dacă o limită s-a
 * schimbat între două adăugări.
 */
export function cartOfferMinimumMissing(lines: readonly CartLine[]): boolean {
  const quantityByOffer = new Map<string, number>();
  const minimumByOffer = new Map<string, number>();
  for (const line of lines) {
    quantityByOffer.set(line.offerId, (quantityByOffer.get(line.offerId) ?? 0) + line.qty);
    minimumByOffer.set(
      line.offerId,
      Math.max(minimumByOffer.get(line.offerId) ?? 1, Math.max(1, line.minQty ?? 1)),
    );
  }
  return [...minimumByOffer].some(([offerId, minimum]) => (quantityByOffer.get(offerId) ?? 0) < minimum);
}

export interface OrderSession {
  storefrontSlug: string | null;
  /** Numele vitrinei, pentru bara de cos; informativ, nu identitate. */
  storefrontName: string | null;
  address: DropoffAddressDto | null;
  acceptedVendorId: string | null;
  lines: CartLine[];
  /** ADR-032: codul de voucher introdus de client; pleaca cu fiecare cotatie. */
  voucherCode: string | null;
}

const EMPTY_SESSION: OrderSession = {
  storefrontSlug: null,
  storefrontName: null,
  address: null,
  acceptedVendorId: null,
  lines: [],
  voucherCode: null,
};

// v2: liniile poarta `lineId` si optiunile alese, comenzile retinute poarta si
// telefonul cerut de urmarire. Un blob v1 nu se reinterpreteaza — ar produce
// linii fara optiuni si o urmarire fara telefon, adica exact defectele reparate.
const SESSION_STORAGE_PREFIX = "deliveryapp.session.v2";
const ORDERS_STORAGE_PREFIX = "deliveryapp.orders.v2";
const MAX_REMEMBERED_ORDERS = 20;

// ---------------------------------------------------------------------------
// Persistenta
// ---------------------------------------------------------------------------

/**
 * Cat poate ajunge o linie: minimul dintre limita ofertei si cea a contractului.
 */
function lineCeiling(maxQty?: number | null): number {
  // `0` inseamna „nu se poate comanda", nu „fara limita". Doar absenta valorii
  // (`null`/`undefined`) inseamna „doar limita generala".
  const offerLimit = typeof maxQty === "number" ? Math.max(0, maxQty) : MAX_QTY_PER_LINE;
  return Math.min(offerLimit, MAX_QTY_PER_LINE);
}

function isSelectedOption(value: unknown): value is CartSelectedOptionInput {
  if (typeof value !== "object" || value === null) return false;
  const option = value as Record<string, unknown>;
  return typeof option.groupId === "string" && typeof option.optionId === "string";
}

function isCartLine(value: unknown): value is CartLine {
  if (typeof value !== "object" || value === null) return false;
  const line = value as Record<string, unknown>;
  return (
    typeof line.lineId === "string" &&
    line.lineId.length > 0 &&
    typeof line.offerId === "string" &&
    typeof line.skuId === "string" &&
    typeof line.name === "string" &&
    Number.isSafeInteger(line.unitPriceGross) &&
    (line.compareAtUnitPrice === undefined ||
      line.compareAtUnitPrice === null ||
      Number.isSafeInteger(line.compareAtUnitPrice)) &&
    Number.isSafeInteger(line.qty) &&
    (line.qty as number) > 0 &&
    Array.isArray(line.selectedOptions) &&
    line.selectedOptions.every(isSelectedOption) &&
    Array.isArray(line.optionLabels) &&
    line.optionLabels.every((label) => typeof label === "string")
  );
}

/**
 * Validare intreaga, nu pe campuri: un blob care nu se potriveste se arunca
 * complet. Reparatul partial ar produce un cos cu linii fara pret, iar clientul
 * ar descoperi asta la checkout.
 */
export function parseSession(raw: string | null): OrderSession {
  if (raw === null) return EMPTY_SESSION;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return EMPTY_SESSION;
    const candidate = parsed as Record<string, unknown>;
    const lines = Array.isArray(candidate.lines) ? candidate.lines : [];
    if (!lines.every(isCartLine)) return EMPTY_SESSION;
    const address =
      typeof candidate.address === "object" && candidate.address !== null
        ? (candidate.address as DropoffAddressDto)
        : null;
    if (address !== null && (typeof address.lat !== "number" || typeof address.lng !== "number")) {
      return EMPTY_SESSION;
    }
    return {
      storefrontSlug: typeof candidate.storefrontSlug === "string" ? candidate.storefrontSlug : null,
      storefrontName: typeof candidate.storefrontName === "string" ? candidate.storefrontName : null,
      address,
      acceptedVendorId: typeof candidate.acceptedVendorId === "string" ? candidate.acceptedVendorId : null,
      lines: lines as CartLine[],
      voucherCode: typeof candidate.voucherCode === "string" && candidate.voucherCode.length > 0 ? candidate.voucherCode : null,
    };
  } catch {
    return EMPTY_SESSION;
  }
}

function readSession(marketKey: string): OrderSession {
  try {
    return parseSession(window.localStorage.getItem(`${SESSION_STORAGE_PREFIX}.${marketKey}`));
  } catch {
    return EMPTY_SESSION;
  }
}

// ---------------------------------------------------------------------------
// Comenzile plasate de pe acest dispozitiv
// ---------------------------------------------------------------------------

/**
 * Comenzile pe care le-a plasat chiar browserul asta.
 *
 * Urmarirea cere codul SI telefonul comenzii (`POST /api/orders/:code/urmarire`),
 * pentru ca in spatele unui cod scurt stau numele, adresa si telefonul
 * clientului. Perechea se retine impreuna, plus — de la v2.1 — ce s-a comandat,
 * ca sa existe „Comanda din nou" si o lista de comenzi fara cont. Campurile noi
 * sunt optionale la citire: intrarile scrise inainte raman valide.
 */
export interface RememberedLine {
  lineId: string;
  offerId: string;
  skuId: string;
  name: string;
  qty: number;
  unitPriceGross: MoneyMinor;
  selectedOptions: CartSelectedOptionInput[];
  optionLabels: string[];
  imageUrl: string | null;
}

export interface RememberedOrder {
  code: string;
  phone: string;
  placedAt?: string | null;
  storefrontSlug?: string | null;
  storefrontName?: string | null;
  sellerName?: string | null;
  total?: MoneyMinor | null;
  lines?: RememberedLine[];
}

function isRememberedOrder(item: unknown): item is RememberedOrder {
  if (typeof item !== "object" || item === null) return false;
  const entry = item as Record<string, unknown>;
  return typeof entry.code === "string" && entry.code.length > 0 && typeof entry.phone === "string" && entry.phone.length > 0;
}

export function readRememberedOrders(marketKey: string): RememberedOrder[] {
  try {
    const raw = window.localStorage.getItem(`${ORDERS_STORAGE_PREFIX}.${marketKey}`);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRememberedOrder);
  } catch {
    return [];
  }
}

function writeRememberedOrders(marketKey: string, list: RememberedOrder[]): void {
  try {
    window.localStorage.setItem(`${ORDERS_STORAGE_PREFIX}.${marketKey}`, JSON.stringify(list.slice(0, MAX_REMEMBERED_ORDERS)));
  } catch {
    // Fara istoric local comanda ramane accesibila prin cod plus telefon.
  }
}

export function rememberOrder(
  marketKey: string,
  code: string,
  phone: string,
  extra: Omit<RememberedOrder, "code" | "phone"> = {},
): void {
  if (code.length === 0 || phone.length === 0) return;
  const existing = readRememberedOrders(marketKey);
  const previous = existing.find((item) => item.code === code);
  // Intrarea existenta nu-si pierde produsele cand urmarirea o rescrie doar cu
  // codul si telefonul.
  const merged: RememberedOrder = { ...(previous ?? {}), ...extra, code, phone };
  writeRememberedOrders(marketKey, [merged, ...existing.filter((item) => item.code !== code)]);
}

export function recentOrderCodes(marketKey: string): string[] {
  return readRememberedOrders(marketKey).map((item) => item.code);
}

/** Telefonul cu care s-a plasat comanda, daca a plecat chiar de pe acest dispozitiv. */
export function recallOrderPhone(marketKey: string, code: string): string | null {
  return readRememberedOrders(marketKey).find((item) => item.code === code)?.phone ?? null;
}

export function rememberedLinesFromCart(lines: readonly CartLine[]): RememberedLine[] {
  return lines.map((line) => ({
    lineId: line.lineId,
    offerId: line.offerId,
    skuId: line.skuId,
    name: line.name,
    qty: line.qty,
    unitPriceGross: line.unitPriceGross,
    selectedOptions: line.selectedOptions,
    optionLabels: line.optionLabels,
    imageUrl: line.imageUrl,
  }));
}

/** Liniile unei comenzi retinute, ca linii de cos NEVERIFICATE (se reverifica la adresa). */
export function cartLinesFromRemembered(lines: readonly RememberedLine[]): CartLine[] {
  return lines
    .filter((line) => Number.isSafeInteger(line.qty) && line.qty > 0)
    .map((line) => ({
      lineId: line.lineId,
      offerId: line.offerId,
      skuId: line.skuId,
      name: line.name,
      unitPriceGross: line.unitPriceGross,
      compareAtUnitPrice: null,
      qty: line.qty,
      notes: null,
      imageUrl: line.imageUrl,
      selectedOptions: line.selectedOptions,
      optionLabels: line.optionLabels,
      verifiedForAddressKey: null,
    }));
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface CartContextValue {
  session: OrderSession;
  itemCount: number;
  subtotal: MoneyMinor;
  /** Exista linii marcate neonorabile la adresa curenta. */
  hasUnavailableLines: boolean;
  setAddress: (address: DropoffAddressDto) => void;
  clearAddress: () => void;
  selectStorefront: (slug: string, name?: string | null) => void;
  /**
   * `maxQty` e limita ofertei, cand exista. Se trimite AICI, nu se verifica in
   * card: doar reducerul cunoaste cantitatea curenta a liniei in momentul
   * scrierii.
   */
  addLine: (line: Omit<CartLine, "qty">, qty: number, maxQty?: number | null) => void;
  setQty: (lineId: string, qty: number, maxQty?: number | null) => void;
  setLineNotes: (lineId: string, notes: string) => void;
  /** Inlocuieste liniile dupa o reverificare contra meniului adresei. */
  replaceLines: (lines: CartLine[]) => void;
  removeUnavailableLines: () => void;
  clearLines: () => void;
  /** Pune in cos liniile unei comenzi anterioare, pe vitrina ei (neverificate). */
  restoreOrder: (storefrontSlug: string, storefrontName: string | null, lines: CartLine[]) => void;
  acceptSeller: (vendorId: string) => void;
  /** ADR-032: pune/scoate codul de voucher; cotatia se reface singura. */
  setVoucherCode: (code: string | null) => void;
  resetSession: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ marketKey, children }: { marketKey: string; children: ReactNode }) {
  const [session, setSession] = useState<OrderSession>(() => readSession(marketKey));

  useEffect(() => {
    try {
      window.localStorage.setItem(`${SESSION_STORAGE_PREFIX}.${marketKey}`, JSON.stringify(session));
    } catch {
      return;
    }
  }, [marketKey, session]);

  const setAddress = useCallback((address: DropoffAddressDto) => {
    // Alta adresa poate insemna alta zona, deci alt vendor. Acceptul precedent
    // nu mai are acoperire si se sterge (ADR-003). Liniile raman, dar marcajul
    // „indisponibil" se scoate: la noua adresa produsul poate fi din nou
    // disponibil, iar reverificarea decide.
    setSession((current) => ({
      ...current,
      address,
      acceptedVendorId: null,
      lines: current.lines.map((line) => (line.unavailableAtAddress === true ? { ...line, unavailableAtAddress: false } : line)),
    }));
  }, []);

  const clearAddress = useCallback(() => {
    setSession((current) => ({ ...current, address: null, acceptedVendorId: null }));
  }, []);

  const setVoucherCode = useCallback((code: string | null) => {
    const normalized = code === null ? null : code.trim().toUpperCase();
    setSession((current) => ({ ...current, voucherCode: normalized !== null && normalized.length > 0 ? normalized : null }));
  }, []);

  const selectStorefront = useCallback((slug: string, name: string | null = null) => {
    setSession((current) => {
      if (current.storefrontSlug === slug) {
        return name === null || current.storefrontName === name ? current : { ...current, storefrontName: name };
      }
      // Ofertele apartin unei vitrine: pastrarea liniilor la schimbarea vitrinei
      // ar trimite la checkout `offerId`-uri care nu exista in noul meniu.
      return { ...current, storefrontSlug: slug, storefrontName: name, lines: [], acceptedVendorId: null };
    });
  }, []);

  const addLine = useCallback((line: Omit<CartLine, "qty">, qty: number, maxQty?: number | null) => {
    if (!Number.isSafeInteger(qty) || qty <= 0) return;
    setSession((current) => {
      const existing = current.lines.find((item) => item.lineId === line.lineId);
      const ceiling = lineCeiling(maxQty);
      const remainingForLine = Math.max(0, ceiling - otherQtyForOffer(current.lines, line.offerId, line.lineId));
      if (ceiling <= 0) return current;
      if (existing === undefined) {
        const nextQty = qtyForNewCartLine(current.lines, line.offerId, line.lineId, qty, maxQty);
        // Cand plafonul agregat e deja consumat, NU persistam o linie qty=0:
        // `parseSession` o refuza la urmatoarea incarcare si ar goli intregul cos.
        if (nextQty <= 0) return current;
        return { ...current, lines: [...current.lines, { ...line, qty: nextQty }] };
      }
      return {
        ...current,
        lines: current.lines.map((item) =>
          item.lineId === line.lineId
            ? {
                ...item,
                ...(maxQty === undefined ? {} : { maxQty }),
                ...(line.minQty === undefined ? {} : { minQty: line.minQty }),
                ...(line.verifiedForAddressKey === undefined ? {} : { verifiedForAddressKey: line.verifiedForAddressKey }),
                unavailableAtAddress: false,
                qty: Math.min(item.qty + qty, remainingForLine),
              }
            : item,
        ),
      };
    });
  }, []);

  const setQty = useCallback((lineId: string, qty: number, maxQty?: number | null) => {
    setSession((current) => {
      const target = current.lines.find((line) => line.lineId === lineId);
      if (target === undefined) return current;
      const ceiling = lineCeiling(maxQty ?? target.maxQty);
      const remainingForLine = Math.max(0, ceiling - otherQtyForOffer(current.lines, target.offerId, lineId));
      return {
        ...current,
        lines:
          qty <= 0 || ceiling <= 0 || remainingForLine <= 0
            ? current.lines.filter((item) => item.lineId !== lineId)
            : current.lines.map((item) =>
                item.lineId === lineId ? { ...item, qty: Math.min(qty, remainingForLine) } : item,
              ),
      };
    });
  }, []);

  const setLineNotes = useCallback((lineId: string, notes: string) => {
    setSession((current) => ({
      ...current,
      lines: current.lines.map((item) =>
        item.lineId === lineId ? { ...item, notes: notes.length > 0 ? notes : null } : item,
      ),
    }));
  }, []);

  const replaceLines = useCallback((lines: CartLine[]) => {
    setSession((current) => ({ ...current, lines: lines.filter((line) => line.qty > 0) }));
  }, []);

  const removeUnavailableLines = useCallback(() => {
    setSession((current) => ({ ...current, lines: current.lines.filter((line) => line.unavailableAtAddress !== true) }));
  }, []);

  const clearLines = useCallback(() => {
    setSession((current) => ({ ...current, lines: [] }));
  }, []);

  const restoreOrder = useCallback((storefrontSlug: string, storefrontName: string | null, lines: CartLine[]) => {
    setSession((current) => ({
      ...current,
      storefrontSlug,
      storefrontName,
      acceptedVendorId: null,
      lines: lines.filter((line) => line.qty > 0),
    }));
  }, []);

  const acceptSeller = useCallback((vendorId: string) => {
    setSession((current) =>
      current.acceptedVendorId === vendorId ? current : { ...current, acceptedVendorId: vendorId },
    );
  }, []);

  const resetSession = useCallback(() => {
    setSession(EMPTY_SESSION);
  }, []);

  const itemCount = useMemo(() => session.lines.reduce((total, line) => total + line.qty, 0), [session.lines]);
  const subtotal = useMemo(
    () => session.lines.reduce((total, line) => total + line.unitPriceGross * line.qty, 0),
    [session.lines],
  );
  const hasUnavailableLines = useMemo(() => session.lines.some((line) => line.unavailableAtAddress === true), [session.lines]);

  const value = useMemo<CartContextValue>(
    () => ({
      session,
      itemCount,
      subtotal,
      hasUnavailableLines,
      setAddress,
      clearAddress,
      selectStorefront,
      addLine,
      setQty,
      setLineNotes,
      replaceLines,
      removeUnavailableLines,
      clearLines,
      restoreOrder,
      acceptSeller,
      setVoucherCode,
      resetSession,
    }),
    [
      session,
      itemCount,
      subtotal,
      hasUnavailableLines,
      setAddress,
      clearAddress,
      selectStorefront,
      addLine,
      setQty,
      setLineNotes,
      replaceLines,
      removeUnavailableLines,
      clearLines,
      restoreOrder,
      acceptSeller,
      setVoucherCode,
      resetSession,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (value === null) {
    throw new Error("useCart a fost apelat in afara lui CartProvider.");
  }
  return value;
}
